/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, JsonPipe } from '@angular/common';
import {
    Component,
    ElementRef,
    HostListener,
    inject,
    Inject,
    OnDestroy,
    OnInit,
    Optional,
    QueryList,
    ViewChildren,
    ViewContainerRef
} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { MatToolbar } from '@angular/material/toolbar';
import { BottomSheetEnabled } from '@app/types/BottomSheetEnabled';
import { AuthService } from '@core/security/services/auth.service';
import { LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';

import { Store } from '@ngxs/store';
import { LoggerService } from '@shared/services/logger.service';

import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { MapConfigState } from '../state/map-config.state';
import { MapToolWidgetConfiguration } from '../types';
import { MapToolbarWidgetComponent } from './widget/map-toolbar-widget.component';

@Component({
    selector: 'dash-gis-map-toolbar',
    templateUrl: './map-toolbar.component.html',
    styleUrls: [ './map-toolbar.component.scss' ],
    imports: [
        MatToolbar,
        LayoutDirective,
        LayoutAlignDirective,
        MapToolbarWidgetComponent,
        MatGridList,
        MatGridTile,
        AsyncPipe,
        JsonPipe
    ]
})
export class MapToolbarComponent implements OnInit, OnDestroy, BottomSheetEnabled {
    public isBottomSheet: boolean;
    public bottomSheetColumnCount$: Observable<number>;

    public visibleWidgets$: Observable<MapToolWidgetConfiguration[]>;

    private mapConfigLoaded$: Observable<boolean> = inject(Store).select(MapConfigState.loaded);
    private toolbarWidgets$: Observable<MapToolWidgetConfiguration[]> = inject(Store).select(
        MapConfigState.toolbarWidgets
    );
    private bottomSheetWidgets$: Observable<MapToolWidgetConfiguration[]> = inject(Store).select(
        MapConfigState.bottomSheetWidgets
    );

    @ViewChildren('widgetTarget', { read: ViewContainerRef })
    private widgetContainers: QueryList<ViewContainerRef>;

    private disposeSubject$ = new Subject<void>();
    private shouldPreventBottomSheetClose = false;

    /**
     * @param elementRef {ElementRef}
     * @param logger {LoggerService}
     * @param authService {AuthService}
     * @param breakpointObserver {BreakpointObserver}
     * @param bottomSheet
     * @param data {any}
     */
    constructor(
        public elementRef: ElementRef,
        private logger: LoggerService,
        private authService: AuthService,
        private breakpointObserver: BreakpointObserver,
        @Optional() private bottomSheet: MatBottomSheetRef,
        @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) private data: any
    ) {
        this.isBottomSheet = false;
        if (this.data) {
            this.isBottomSheet = this.data.isBottomSheet || false;
        }

        this.visibleWidgets$ = this.getWidgets$();
        this.bottomSheetColumnCount$ = this.visibleWidgets$.pipe(
            map((visibleWidgets) => {
                if (this.breakpointObserver.isMatched(Breakpoints.XSmall)) {
                    return Math.min(5, visibleWidgets.length);
                }
                if (this.breakpointObserver.isMatched(Breakpoints.Small)) {
                    return Math.min(6, visibleWidgets.length);
                }
                if (this.breakpointObserver.isMatched(Breakpoints.Medium)) {
                    return Math.min(8, visibleWidgets.length);
                }
                if (this.breakpointObserver.isMatched(Breakpoints.Large)) {
                    return Math.min(12, visibleWidgets.length);
                }
            })
        );
    }

    ngOnInit() {}

    ngOnDestroy(): void {
        this.disposeSubject$.next();
    }

    @HostListener('click')
    clickListener() {
        if (this.isBottomSheet && this.bottomSheet && !this.shouldPreventBottomSheetClose) {
            this.bottomSheet.dismiss();
        }
    }

    handleShouldPreventClose(shouldPrevent: boolean) {
        this.shouldPreventBottomSheetClose = shouldPrevent;
    }

    getWidgets$(): Observable<MapToolWidgetConfiguration[]> {
        return this.mapConfigLoaded$.pipe(
            distinctUntilChanged(),
            filter((loaded) => loaded),
            switchMap(() => (this.isBottomSheet ? this.bottomSheetWidgets$ : this.toolbarWidgets$)),
            map((widgets) => widgets.filter((w) => this.authService.isAuthorized(w.roles || [])))
        );
    }
}
